/**
 * LeadsLens — lens-switcher for the canonical Leads surface (/growth-channels).
 *
 * Two lenses, both rendered under the single "Leads" cockpit masthead:
 *   • performance — analytics cockpit (Lead Sources / Attribution / Campaigns / Call Center)
 *   • response    — lead-response engine surface (Sources / Flows / AI / Autopilot / Command View)
 *
 * Resolution order: ?lens=… → localStorage → role default.
 * Writes go to both URL (replace) and localStorage so the next visit lands the
 * same way and links remain shareable.
 *
 * Available lenses are derived from existing RBAC. No RBAC changes here.
 *   - has /growth-channels        → "performance" available
 *   - has /pipeline-intelligence  → "response" available
 *   - one-of                      → no toggle (single-lens role)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useRole, type UserRole } from "@/data/RoleContext";
import { isRouteAllowed } from "@/data/rolePermissions";
import { cn } from "@/lib/utils";
import { BarChart3, Zap } from "lucide-react";

export type LeadsLensId = "performance" | "response";

const STORAGE_KEY = "leads:lens";
const VALID = new Set<LeadsLensId>(["performance", "response"]);

function readStored(): LeadsLensId | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw && VALID.has(raw as LeadsLensId) ? (raw as LeadsLensId) : null;
  } catch { return null; }
}

function writeStored(v: LeadsLensId) {
  try { window.localStorage.setItem(STORAGE_KEY, v); } catch { /* non-fatal */ }
}

/** Role-based default. Marketing-style roles prefer Response; the rest prefer
 *  Performance. The role enum has no `marketing` today — kept as a no-op slot
 *  so the rule activates if/when it lands. */
function defaultFor(role: UserRole): LeadsLensId {
  if (String(role).includes("marketing")) return "response";
  return "performance";
}

function availableFor(role: UserRole): LeadsLensId[] {
  const lenses: LeadsLensId[] = [];
  if (isRouteAllowed(role, "/growth-channels")) lenses.push("performance");
  if (isRouteAllowed(role, "/pipeline-intelligence")) lenses.push("response");
  return lenses;
}

export function useLeadsLens() {
  const { role } = useRole();
  const [searchParams, setSearchParams] = useSearchParams();

  const available = useMemo(() => availableFor(role), [role]);
  const fallback: LeadsLensId = available[0] ?? "performance";
  const roleDefault = useMemo<LeadsLensId>(() => {
    const d = defaultFor(role);
    return available.includes(d) ? d : fallback;
  }, [role, available, fallback]);

  const urlLens = searchParams.get("lens");
  const initial = useMemo<LeadsLensId>(() => {
    if (urlLens && VALID.has(urlLens as LeadsLensId) && available.includes(urlLens as LeadsLensId)) {
      return urlLens as LeadsLensId;
    }
    const stored = readStored();
    if (stored && available.includes(stored)) return stored;
    return roleDefault;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // resolve once on mount; subsequent changes flow through setLens

  const [lens, setLensState] = useState<LeadsLensId>(initial);

  // Mirror lens to URL (replace, no remount) and localStorage.
  useEffect(() => {
    writeStored(lens);
    const next = new URLSearchParams(searchParams);
    if (next.get("lens") !== lens) {
      next.set("lens", lens);
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lens]);

  // React to URL changes from in-app navigation (e.g. back/forward, deep links).
  useEffect(() => {
    if (urlLens && VALID.has(urlLens as LeadsLensId) && urlLens !== lens && available.includes(urlLens as LeadsLensId)) {
      setLensState(urlLens as LeadsLensId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlLens]);

  const setLens = useCallback((v: LeadsLensId) => {
    if (!available.includes(v)) return;
    setLensState(v);
  }, [available]);

  return { lens, setLens, available, showSwitch: available.length > 1 };
}

/** Compact segmented toggle for the cockpit masthead `controls` slot. */
export function LeadsLensSwitch({
  value, onChange, available,
}: { value: LeadsLensId; onChange: (v: LeadsLensId) => void; available: LeadsLensId[] }) {
  if (available.length <= 1) return null;
  return (
    <div
      role="tablist"
      aria-label="Leads lens"
      className="inline-flex items-center rounded-lg border border-border/60 bg-muted/30 p-0.5"
    >
      {available.includes("performance") && (
        <Tab active={value === "performance"} onClick={() => onChange("performance")}
             icon={<BarChart3 className="h-3.5 w-3.5" />} label="Performance" />
      )}
      {available.includes("response") && (
        <Tab active={value === "response"} onClick={() => onChange("response")}
             icon={<Zap className="h-3.5 w-3.5" />} label="Response" />
      )}
    </div>
  );
}

function Tab({ active, onClick, icon, label }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold transition-all duration-200",
        active
          ? "bg-primary text-primary-foreground shadow-md ring-1 ring-primary/40"
          : "text-muted-foreground hover:text-foreground hover:bg-background/60",
      )}
    >
      {icon}
      {label}
    </button>
  );
}
